import React, { useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import withStyles from "@mui/styles/withStyles";
import styles from "../styles";
import {ANDatePicker} from "@answersnow/pkg-theme";
import { useGlobalToast } from "components/GlobalToastProvider";
import useToggleClientActivation from "hooks/api/client/useToggleClientActivation";
import { Box } from "@mui/material";
import actions from "actions";
import ClientDeactivationDialog from "../Dialogs/ClientDeactivationDialog";

const ClientActivation = (props) => {
  const {
    onClearDeactivationInfo,
    isActive,
    hasScheduledCall,
    userPermissions,
    clientId,
    customerDetails,
    isFutureDeactivationEdit,
    onDeactivationSuccess,
    hideButton,
    isConfirmingScheduledDeactivation,
    childName,
    childLastName,
  } = props;

  const dispatch = useDispatch();

  // Extract the client's user ID from customerDetails
  const userId = customerDetails?.details?.userid;

  // State for dialog visibility
  const [activationDialogOpen, setActivationDialogOpen] = useState(false);
  const [deactivationDialogOpen, setDeactivationDialogOpen] = useState(false);

  // Use refs to track if we've already processed the parent's open request
  const prevFutureEditRef = React.useRef(false);
  const prevConfirmingRef = React.useRef(false);

  // Handle auto-opening when parent sets props
  React.useEffect(() => {
    const shouldAutoOpen = isFutureDeactivationEdit || isConfirmingScheduledDeactivation;
    const hasChangedToTrue =
      (isFutureDeactivationEdit && !prevFutureEditRef.current) ||
      (isConfirmingScheduledDeactivation && !prevConfirmingRef.current);

    if (shouldAutoOpen && hasChangedToTrue) {
      setDeactivationDialogOpen(true);
    }

    prevFutureEditRef.current = isFutureDeactivationEdit;
    prevConfirmingRef.current = isConfirmingScheduledDeactivation;
  }, [isFutureDeactivationEdit, isConfirmingScheduledDeactivation]);

  // Dialog open/close handlers
  const handleButtonClick = () => {
    if (isActive) {
      setDeactivationDialogOpen(true);
    } else {
      setActivationDialogOpen(true);
    }
  };
  const handleActivationDialogClose = () => setActivationDialogOpen(false);

  const handleDeactivationDialogClose = () => {
    setDeactivationDialogOpen(false);
    // Clear the parent's deactivation info to prevent infinite loop
    onClearDeactivationInfo?.();
  };

  const { showToast } = useGlobalToast();

  // Initialize the toggle activation mutation hook for activation only
  const { toggleActivation, isLoadingToggleActivation } =
    useToggleClientActivation({
      onSuccess: () => {
        showToast({
          message: "Success! Client account activated",
        });
        handleActivationDialogClose();
        dispatch(actions.loadCustomerDetails(clientId));
        onDeactivationSuccess?.();
      },
    });

  const activationHandler = useCallback(() => {
    if (!userId || !clientId) {
      showToast({
        message: "Error: Unable to process request. Missing client information.",
        errorState: true,
      });
      return;
    }

    toggleActivation({
      id: userId,
      clientId,
    });
  }, [userId, clientId, toggleActivation, showToast]);

  const reactivationReq = !isActive && userPermissions.reactivate_clients;
  const deactivationReq = isActive && userPermissions.disable_client;

  // Determine the trigger mode for the deactivation dialog
  const deactivationTriggerMode = useMemo(() => {
    if (isFutureDeactivationEdit) {
      return "edit";
    }
    if (isConfirmingScheduledDeactivation) {
      return "confirm_from_alert";
    }
    return "new_deactivation";
  }, [isFutureDeactivationEdit, isConfirmingScheduledDeactivation]);

  return (
    <Box minWidth={"max-content"}>
      {!hideButton && (
        <Button
          variant="contained"
          color={isActive ? "error" : "secondary"}
          onClick={handleButtonClick}
          fullWidth
          disabled={!reactivationReq && !deactivationReq}
        >
          {isActive ? "Deactivate Client" : "Activate Client"}
        </Button>
      )}

      {/* Activation dialog - simple confirmation for reactivating clients */}
      <Dialog open={activationDialogOpen} onClose={handleActivationDialogClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Activate Client</DialogTitle>
        <DialogContent>
          <Typography>
            Would you like to activate the Primary Account holder for this client?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleActivationDialogClose}
            variant="outlined"
            color="default"
          >
            Cancel
          </Button>
          <Button
            disabled={isLoadingToggleActivation}
            onClick={activationHandler}
            color="primary"
            variant="contained"
          >
            {isLoadingToggleActivation ? "Processing..." : "Activate client"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ClientDeactivationDialog - unified for all deactivation flows */}
      <ClientDeactivationDialog
        open={deactivationDialogOpen}
        onClose={handleDeactivationDialogClose}
        clientId={clientId}
        clientName={childName}
        clientLastName={childLastName}
        triggerMode={deactivationTriggerMode}
        onSuccess={onDeactivationSuccess}
        customerDetails={customerDetails}
        hasScheduledCall={hasScheduledCall}
      />
    </Box>
  );
};

export default withStyles(styles)(ClientActivation);
